import React, { useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { getApp } from 'firebase/app';
import {
  getFirestore, collection, doc, getDoc, getDocs, query, where,
  getCountFromServer, Timestamp, orderBy, limit, startAfter, documentId,
} from 'firebase/firestore';
import './analytics.css';
import MyStorage from './util/storage';
// Side-effect import: firebase.js calls compat initializeApp(), which registers
// the default app in the same registry getApp() reads from. Without this the
// getFirestore() call below would run before any app exists.
import '../../firebase/firebase';

/**
 * Analytics dashboard — ported from the Eat2Live standalone admin page
 * (Eat2Live/admin/index.html) into this admin section.
 *
 * Two deliberate departures from the original:
 *
 *  • No Firebase Auth gate. The standalone page had to authenticate itself
 *    because it was served as a bare HTML file; here the whole /admin section
 *    is already behind MyStorage.isLogin(), the same guard every other admin
 *    page uses, so a second unrelated login would just be a second password to
 *    forget. The redirect below matches nav.js.
 *
 *  • Modular Firestore SDK on the compat app. The rest of this project uses the
 *    compat API (db.collection(...)), but this page leans on count aggregations
 *    and documentId() paging, which read far more naturally in the modular API —
 *    and keeping the query code identical to the original is what makes the
 *    comments below still true. Both APIs talk to the same initialised app, so
 *    they share one connection and one auth state.
 *
 * The rendering is imperative (innerHTML into fixed ids) exactly as in the
 * original. That is why this component holds no state: React renders the shell
 * once and never re-reconciles it, so the DOM the effect writes into is stable.
 * Adding a useState here would make React blow away everything the effect drew.
 */
const Analytics = () => {
  const rootRef = useRef(null);

  // Hooks must run unconditionally, so the session check that produces the
  // redirect below cannot short-circuit before this point.
  const loggedIn = MyStorage.isLogin();

  useEffect(() => {
    if (!loggedIn) return undefined;
    const root = rootRef.current;
    if (!root) return undefined;

    const db = getFirestore(getApp());
    let disposed = false;

    /**
     * The collection holding one document per end-user account.
     *
     * The dashboard was written against a project whose accounts lived in
     * `users`; here they live in `patients` (see cmp/api — every other admin
     * page reads that collection). The per-account subcollections the detail
     * panel opens, `daily_activity` and `activity_log`, hang off the same
     * document, so this one name covers all of it.
     */
    const ACCOUNTS = 'patients';

    // Everything is bucketed by UTC day, matching the analytics writer on the
    // device side. Using the browser's local midnight here would make the
    // dashboard and the devices disagree about which day "today" was.
    function startOfTodayTs() {
      const n = new Date();
      return Timestamp.fromDate(new Date(Date.UTC(n.getUTCFullYear(), n.getUTCMonth(), n.getUTCDate())));
    }
    const utcDayId = (d) => d.toISOString().slice(0, 10);
    const todayId = () => utcDayId(new Date());
    const num = (o, k) => Number((o && o[k]) || 0);
    const byId = (id) => root.querySelector('#' + id);
    const setText = (id, v) => { const el = byId(id); if (el) el.textContent = v; };
    /**
     * Percentage for a funnel step. Returns '—' rather than 0% or NaN when the
     * denominator is zero, so an empty range reads as "no data" instead of as a
     * real 0% conversion — those mean very different things.
     */
    const pct = (part, whole) => (!whole ? '—' : Math.round((part / whole) * 100) + '%');

    // ── Platform segmentation ────────────────────────────────────────────────
    //
    // 'all' | 'ios' | 'android'. Governs every number on the page.
    //
    // The app writes each aggregate counter twice — `mealsLogged` and
    // `mealsLogged_ios` — into the same shard document. So segmenting the
    // counters is a change of field name, not a second query: the reads below
    // are identical in cost whichever platform is selected.
    //
    // Two things this cannot do, and the note under the toggle says so:
    //   • The suffixed counters only exist for days AFTER the platform-aware
    //     build shipped. Older days have the global field only, so they read as
    //     zero under iOS/Android while All still shows the real number.
    //   • installs/{id}.platform and users/{uid}.platform are re-sent on every
    //     launch, so they backfill themselves — but only for devices that are
    //     still opened. A churned install stays unattributed and shows up under
    //     All and in the Unknown row, never under a platform.
    let platformFilter = 'all';

    /** 'mealsLogged' -> 'mealsLogged_ios' when a platform is selected. */
    const pf = (field) => (platformFilter === 'all' ? field : field + '_' + platformFilter);
    const platformLabel = () => (platformFilter === 'ios' ? 'iOS' : platformFilter === 'android' ? 'Android' : 'All platforms');

    /**
     * Adds the platform equality to a query when one is selected.
     *
     * Equality-only queries (platform + country, platform + subscription) are
     * served by index merging over the automatic single-field indexes, so they
     * need no composite index. The two that pair platform with a TIMESTAMP
     * RANGE — new installs and active users — do; see safeCount.
     */
    const withPlatform = (col, ...clauses) =>
      (platformFilter === 'all'
        ? query(col, ...clauses)
        : query(col, ...clauses, where('platform', '==', platformFilter)));

    // Counts that may legitimately fail (a missing composite index) must not
    // take the whole overview down with them. Returns null and remembers why,
    // so the toggle's note can say what happened instead of the card silently
    // showing a wrong zero.
    let countIssue = null;
    async function safeCount(q, what) {
      try {
        return (await getCountFromServer(q)).data().count;
      } catch (e) {
        countIssue = what;
        // Firestore puts a one-click "create index" URL in this message.
        console.warn(`count failed (${what}) — if this mentions an index, follow the link in the error:`, e);
        return null;
      }
    }
    const showCount = (id, v) => setText(id, v === null ? '⚠' : v);
    const addDays = (id, delta) => {
      const d = new Date(id + 'T00:00:00Z');
      d.setUTCDate(d.getUTCDate() + delta);
      return utcDayId(d);
    };
    // Every UTC day id from `from` to `to` inclusive, capped so a fat-fingered
    // range can't try to render (or read) thousands of days.
    function dayRange(from, to, maxDays = 92) {
      const ids = [];
      let cur = from;
      while (cur <= to && ids.length < maxDays) {
        ids.push(cur);
        cur = addDays(cur, 1);
      }
      return ids;
    }

    // Counters are sharded across daily_stats/{date}/shards and
    // stats/summary/shards, because one document only sustains ~1 write/second.
    // The parent doc is read and added in too, so any pre-sharding history
    // written directly to it still counts.
    async function sumCounters(parentRef) {
      const [legacySnap, shardsSnap] = await Promise.all([
        getDoc(parentRef),
        getDocs(collection(parentRef, 'shards')),
      ]);
      const totals = {};
      const add = (data) => {
        if (!data) return;
        for (const [k, v] of Object.entries(data)) {
          if (typeof v === 'number') totals[k] = (totals[k] || 0) + v;
        }
      };
      add(legacySnap.exists() ? legacySnap.data() : null);
      shardsSnap.forEach(d => add(d.data()));
      return totals;
    }

    // ── Range picker state ──
    const fromInput = byId('range-from');
    const toInput   = byId('range-to');
    // The Users filter and the Platform toggle both reuse .preset-btn purely for
    // styling; sweeping either into the date-range picker would call
    // applyPreset(undefined) on click and wipe the picker's own state.
    // Captured once, before any user detail panel exists, so the presets those
    // panels render never end up in here either.
    const presetBtns = Array.from(root.querySelectorAll('.preset-btn:not(.uf-btn):not(.plat-btn)'));

    function applyPreset(preset) {
      const today = todayId();
      let from = today;
      const to = today;
      if (preset === '7') from = addDays(today, -6);
      else if (preset === '30') from = addDays(today, -29);
      else if (preset === 'month') from = today.slice(0, 8) + '01';
      fromInput.value = from;
      toInput.value = to;
      presetBtns.forEach(b => b.classList.toggle('active', b.dataset.preset === preset));
      loadOverview();
    }
    presetBtns.forEach(b => b.addEventListener('click', () => applyPreset(b.dataset.preset)));

    // ── Users filter ── purely client-side over the rows already in memory.
    root.querySelectorAll('.uf-btn').forEach(b => b.addEventListener('click', () => {
      userFilter = b.dataset.uf;
      root.querySelectorAll('.uf-btn').forEach(o => o.classList.toggle('active', o === b));
      renderUserRows();
    }));
    // ── Platform toggle ──
    // Re-runs the overview (counter field names change) and re-renders the user
    // views from memory. It deliberately does NOT re-fetch users: the platform
    // is already on every row, so filtering is free.
    root.querySelectorAll('.plat-btn').forEach(b => b.addEventListener('click', () => {
      if (platformFilter === b.dataset.plat) return;
      platformFilter = b.dataset.plat;
      root.querySelectorAll('.plat-btn').forEach(o => o.classList.toggle('active', o === b));
      loadOverview();
      renderUserRows();
      renderPayingCard();
      renderMarkets();
    }));

    function renderPlatformNote() {
      const parts = [];
      if (platformFilter !== 'all') {
        parts.push(`Showing ${platformLabel()} only`);
        // Said plainly, because a zero here is the one number an operator is
        // most likely to misread as "nobody used it".
        parts.push('per-platform counters start the day the platform-aware build shipped — earlier days count under All only');
      }
      if (countIssue) parts.push(`⚠ ${countIssue} unavailable — check the console for a Firestore index link`);
      setText('platform-note', parts.join(' · '));
    }

    [fromInput, toInput].forEach(el => el.addEventListener('change', () => {
      presetBtns.forEach(b => b.classList.remove('active'));
      if (fromInput.value && toInput.value && fromInput.value <= toInput.value) loadOverview();
    }));

    // ── Overview ──
    async function loadOverview() {
      try {
        const installsCol = collection(db, 'installs');
        const start = startOfTodayTs();
        countIssue = null;

        // Total installs is a plain equality on `platform` and always works.
        // The other two pair that equality with a timestamp range, which is the
        // one shape Firestore will not serve from merged single-field indexes —
        // they need a composite index per platform-scoped field. Until it
        // exists they report '⚠' rather than 0.
        const [totalInstalls, newInstalls, activeUsers] = await Promise.all([
          safeCount(withPlatform(installsCol), 'total installs'),
          safeCount(withPlatform(installsCol, where('createdAt', '>=', start)), 'new installs today'),
          safeCount(withPlatform(installsCol, where('lastActiveAt', '>=', start)), 'active users today'),
        ]);
        if (disposed) return;
        showCount('c-total-installs', totalInstalls);
        showCount('c-new-installs',   newInstalls);
        showCount('c-active-users',   activeUsers);

        const from = fromInput.value || todayId();
        const to   = toInput.value   || todayId();
        const dayIds = dayRange(from, to);

        const [summary, days] = await Promise.all([
          sumCounters(doc(db, 'stats', 'summary')),
          Promise.all(dayIds.map(async id => {
            const totals = await sumCounters(doc(db, 'daily_stats', id));
            return { ...totals, date: id };
          })),
        ]);
        if (disposed) return;

        // Both helpers resolve the field through pf(), so selecting a platform
        // reads `mealsLogged_ios` out of the very same documents.
        const sum   = f => days.reduce((s, d) => s + num(d, pf(f)), 0);
        const total = f => num(summary, pf(f));

        setText('c-meals-range',   sum('mealsLogged'));
        setText('c-meals-total',   total('totalMealsLogged'));
        setText('c-scans-range',   sum('foodScans'));
        setText('c-scans-total',   total('totalFoodScans'));
        setText('c-recipes-range', sum('recipeViews'));
        setText('c-recipe-likes',  sum('recipeLikes'));
        setText('c-plans-range',   sum('mealPlans'));
        setText('c-plans-total',   total('totalMealPlans'));
        setText('c-quizzes-range', sum('quizzesTaken'));
        setText('c-learn-range',   sum('learnViews'));
        setText('c-products-range', sum('productViews'));
        setText('c-products-total', total('totalProductViews'));
        setText('c-logins-range',   sum('logins'));
        setText('c-logins-total',   total('totalLogins'));

        // ── Funnel ──
        // Every stage is summed over the SAME day range, so the ratios compare
        // like with like. They are stage-to-stage rates, not a cohort: a signup
        // today may have installed before the range started, which is why these
        // read as trends over a window, not as one cohort's journey.
        const fInstalls    = sum('installs');
        const fSignups     = sum('newSignups');
        const fActivations = sum('activations');
        const fPaywall     = sum('paywallViews');
        const fTaps        = sum('subscribeTaps');
        const fPurchases   = sum('purchases');
        const fFailures    = sum('purchaseFailures');
        // 'Signups' counts anonymous session creation, which happens on the same
        // launch as the install — so it is ~100% by construction. 'accountsLinked'
        // is the one that means the user could actually restore on a new device.
        const fAccounts    = sum('accountsLinked');
        // Paywall views reached from the hard wall rather than opened voluntarily.
        const fGated       = sum('paywallGated');

        setText('f-installs',    fInstalls);
        setText('f-signups',     fSignups);
        setText('f-activations', fActivations);
        setText('f-paywall',     fPaywall);
        setText('f-purchases',   fPurchases);
        setText('f-failures',    fFailures);
        setText('f-accounts',    fAccounts);
        setText('f-gated',       fGated);
        setText('f-signup-rate',     pct(fSignups, fInstalls));
        setText('f-account-rate',    pct(fAccounts, fSignups));
        setText('f-gated-share',     pct(fGated, fPaywall));
        setText('f-activation-rate', pct(fActivations, fSignups));
        setText('f-tap-rate',        pct(fTaps, fPaywall));
        setText('f-purchase-rate',   pct(fPurchases, fTaps));
        setText('range-label', dayIds.length === 1 ? dayIds[0] : `${dayIds[0]} → ${dayIds[dayIds.length - 1]} (${dayIds.length} days)`);

        renderChart(days);
        renderPlatformNote();

        const now = new Date();
        setText('an-last-updated', 'Updated ' + now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
      } catch (e) {
        console.error('Overview load failed', e);
        setText('an-last-updated', 'Load failed — check console');
      }
    }

    // Three grouped bars per day: meals, recipe views, content views.
    function renderChart(days) {
      const svg = byId('trend-chart');
      if (!svg) return;
      svg.innerHTML = '';
      if (!days.length) {
        svg.setAttribute('viewBox', '0 0 600 200');
        svg.removeAttribute('width');
        svg.innerHTML = '<text x="16" y="40" fill="#9aa6b0" font-size="14">No activity yet — data appears once the app starts reporting.</text>';
        return;
      }
      // Deliberately not plotting foodScans alongside mealsLogged: the scanner
      // fires both, so they are the same bar twice until the legacy
      // calorie_counts path is revived. Content views earn the slot instead.
      const series = [
        { field: 'mealsLogged', color: '#219653' },
        { field: 'recipeViews', color: '#2CBE7B' },
        { field: 'learnViews',  color: '#6FCF97' },
      ];
      const barW = 13, gap = 3;
      const groupW = series.length * barW + (series.length - 1) * gap + 18;
      const H = 200, padTop = 24, padBottom = 24;
      const W = Math.max(days.length * groupW + 20, 320);
      svg.setAttribute('viewBox', `0 0 ${W} ${H}`);
      svg.setAttribute('width', W);
      const chartH = H - padTop - padBottom;
      const max = Math.max(1, ...days.flatMap(d => series.map(s => num(d, pf(s.field)))));
      const base = padTop + chartH;

      let out = '';
      days.forEach((d, i) => {
        const x0 = 16 + i * groupW;
        series.forEach((s, j) => {
          const v = num(d, pf(s.field));
          const h = Math.round((v / max) * chartH);
          const x = x0 + j * (barW + gap);
          out += `<rect x="${x}" y="${base - h}" width="${barW}" height="${h}" rx="3" fill="${s.color}"></rect>`;
          if (v) out += `<text x="${x + barW / 2}" y="${base - h - 4}" text-anchor="middle" font-size="9" fill="#607d8b">${v}</text>`;
        });
        const label = (d.date || '').slice(5);
        const mid = x0 + (series.length * barW + (series.length - 1) * gap) / 2;
        out += `<text x="${mid}" y="${H - 6}" text-anchor="middle" font-size="10" fill="#9aa6b0">${label}</text>`;
      });
      svg.innerHTML = out;
    }

    // ── Users ──
    function escapeHtml(s) {
      return String(s === null || s === undefined ? '' : s)
        .replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    }
    // Log entries carry a stable action key ("logged_meal"), never a
    // human-readable sentence — meal descriptions, scan results and recipe
    // titles deliberately never leave the device. Turn the key into a label.
    function prettyAction(action) {
      if (!action) return '—';
      const s = String(action).replace(/_/g, ' ');
      return s.charAt(0).toUpperCase() + s.slice(1);
    }

    function timeAgo(ms) {
      if (!ms) return null;
      const s = Math.round((Date.now() - ms) / 1000);
      if (s < 60) return 'just now';
      if (s < 3600) return Math.floor(s / 60) + 'm ago';
      if (s < 86400) return Math.floor(s / 3600) + 'h ago';
      if (s < 86400 * 30) return Math.floor(s / 86400) + 'd ago';
      return new Date(ms).toLocaleDateString([], { dateStyle: 'medium' });
    }

    // uid -> { open, from, to } — remembers each user's detail panel state
    // across refreshes so re-opening a row doesn't reset its range.
    const userPanelState = {};

    // Rows are held after the fetch so the filter buttons can re-render from
    // memory. Filtering must never re-query: the users list is one read per
    // account and clicking through four filters would quadruple it.
    // Accounts are fetched in full (up to MAX_USERS) so that every derived
    // number — the Paying card, the Markets table, the filter counts — is
    // computed over the whole user base rather than an arbitrary slice.
    // Only the rendered table is capped, because painting thousands of <tr>
    // elements is what actually hurts, not holding them in memory.
    const USER_PAGE = 500;
    const MAX_USERS = 5000;
    const RENDER_LIMIT = 200;
    let userRows = [];
    let userTotalCount = 0;   // authoritative server count, independent of MAX_USERS
    let userTruncated = false;
    let userFilter = 'all';

    function renderUserRows() {
      const tbody = byId('users-tbody');
      if (!tbody) return;
      const visible = userRows.filter(r => matchesPlatform(r.data) && matchesUserFilter(r.data, userFilter));
      const shown = visible.slice(0, RENDER_LIMIT);
      // The server-side account total covers every platform, so it is only a
      // valid denominator for the All view. A platform view counts the rows in
      // memory instead — exact unless MAX_USERS was hit, which is already
      // called out below.
      const total = platformFilter === 'all'
        ? (userTotalCount || userRows.length)
        : userRows.filter(r => matchesPlatform(r.data)).length;

      if (!visible.length) {
        const why = platformFilter === 'all'
          ? 'No accounts match this filter.'
          : `No ${platformLabel()} accounts match this filter — accounts are attributed to a platform on their next app launch.`;
        tbody.innerHTML = `<tr><td colspan="10" style="text-align:center;color:#9aa6b0;padding:24px;">${why}</td></tr>`;
        setText('users-summary', `0 of ${total} account(s)`);
        return;
      }
      tbody.innerHTML = shown.map(r => renderUserRow(r.id, r.data)).join('');

      const parts = [];
      if (userFilter === 'all') parts.push(`${total} account(s)`);
      else parts.push(`${visible.length} of ${total} account(s)`);
      if (platformFilter !== 'all') parts.push(platformLabel());
      if (visible.length > shown.length) parts.push(`showing the ${shown.length} most recently active`);
      else parts.push('most recently active first');
      if (userTruncated) parts.push(`⚠ only the first ${MAX_USERS} were loaded — per-country and filter counts are partial`);
      setText('users-summary', parts.join(' · '));

      // Detail panels are rebuilt as collapsed markup above, so re-open the
      // ones that were expanded — but only those still on screen.
      shown.forEach(r => {
        const st = userPanelState[r.id];
        if (st && st.open) openUserPanel(r.id);
      });
    }

    async function loadUsers() {
      const tbody = byId('users-tbody');
      try {
        const usersCol = collection(db, ACCOUNTS);

        // The authoritative account total, independent of how many documents we
        // then page through. The Paying card's denominator uses this, so the
        // headline conversion rate stays correct even if the page cap is hit.
        try {
          userTotalCount = (await getCountFromServer(usersCol)).data().count;
        } catch (e) {
          console.warn('account count failed', e);
          userTotalCount = 0;
        }

        // Paged over documentId(), NOT lastActiveAt: Firestore drops documents
        // that lack the ordered field, so ordering by lastActiveAt would hide
        // every account created before that field existed. Document id is
        // always present, so paging over it is guaranteed to visit every
        // account exactly once. Rows are sorted by activity client-side below,
        // where a missing field sinks a row to the bottom rather than hiding it.
        const rows = [];
        let cursor = null;
        userTruncated = false;
        while (rows.length < MAX_USERS) {
          const q = cursor
            ? query(usersCol, orderBy(documentId()), startAfter(cursor), limit(USER_PAGE))
            : query(usersCol, orderBy(documentId()), limit(USER_PAGE));
          const page = await getDocs(q);
          if (disposed) return;
          if (page.empty) break;
          page.docs.forEach(d => rows.push({ id: d.id, data: d.data() }));
          cursor = page.docs[page.docs.length - 1];
          if (page.size < USER_PAGE) break;
        }
        if (rows.length >= MAX_USERS && userTotalCount > rows.length) {
          userTruncated = true;
          console.warn(`Users: loaded ${rows.length} of ${userTotalCount} — raise MAX_USERS for exact per-country figures`);
        }

        if (!rows.length) {
          userRows = [];
          setText('users-summary', '0 account(s)');
          if (tbody) tbody.innerHTML = '<tr><td colspan="10" style="text-align:center;color:#9aa6b0;padding:24px;">No accounts yet.</td></tr>';
          // Still refresh the dependants, or they keep stale numbers from a
          // previous load rather than showing zero.
          renderPayingCard();
          renderMarkets();
          return;
        }

        userRows = rows.sort((a, b) => {
          const am = a.data.lastActiveAt && a.data.lastActiveAt.toMillis ? a.data.lastActiveAt.toMillis() : -1;
          const bm = b.data.lastActiveAt && b.data.lastActiveAt.toMillis ? b.data.lastActiveAt.toMillis() : -1;
          return bm - am;
        });
        renderUserRows();
        renderPayingCard();
        renderMarkets();
      } catch (e) {
        console.error('Users load failed', e);
        if (tbody) tbody.innerHTML = '<tr><td colspan="10" style="text-align:center;color:#9aa6b0;padding:24px;">Could not load users — check console.</td></tr>';
      }
    }

    /**
     * Classifies an account for the Trial column and the filter buttons.
     *
     * `trialEndsAt` and `subscription.isSubscribed` are mirrored onto
     * users/{uid} by the app's trial sync, so this needs no extra read.
     * Accounts that haven't yet run a build new enough to write them come back
     * 'unknown' rather than being guessed at — better a dash than a wrong
     * expiry date.
     */
    function trialInfo(u) {
      if (u.subscription && u.subscription.isSubscribed === true) {
        return { state: 'premium', label: 'Premium' };
      }
      const endsAt = typeof u.trialEndsAt === 'number' ? u.trialEndsAt : null;
      if (endsAt === null) return { state: 'unknown', label: '—' };

      const msLeft = endsAt - Date.now();
      if (msLeft <= 0) {
        const daysAgo = Math.floor(-msLeft / 86400000);
        return { state: 'expired', label: daysAgo <= 0 ? 'Expired today' : `Expired ${daysAgo}d ago` };
      }
      const daysLeft = Math.ceil(msLeft / 86400000);
      return {
        // "ending" is only a display accent; the filter treats it as on-trial.
        state: daysLeft <= 2 ? 'ending' : 'active',
        label: daysLeft === 1 ? '1 day left' : `${daysLeft} days left`,
      };
    }

    /**
     * The account's platform, as written on every launch.
     *
     * Accounts on a build older than the platform-aware one — or that have not
     * been opened since it shipped — have no field yet and read as 'unknown'
     * rather than being guessed at. They are excluded from both the iOS and the
     * Android views on purpose: an account assigned to the wrong store is worse
     * than one that is visibly unattributed.
     */
    function userPlatform(u) {
      const p = typeof u.platform === 'string' ? u.platform.toLowerCase() : '';
      return p === 'ios' || p === 'android' ? p : 'unknown';
    }
    const matchesPlatform = u => platformFilter === 'all' || userPlatform(u) === platformFilter;

    function matchesUserFilter(u, filter) {
      if (filter === 'all') return true;
      const t = trialInfo(u);
      if (filter === 'premium') return t.state === 'premium';
      if (filter === 'expired') return t.state === 'expired';
      if (filter === 'trial') return t.state === 'active' || t.state === 'ending';
      return true;
    }

    const isPaying = u => !!(u.subscription && u.subscription.isSubscribed === true);

    /**
     * Paying-customer count.
     *
     * Uses a server-side count aggregation rather than tallying the rows in
     * memory. `subscription.isSubscribed` is a nested field, which Firestore
     * indexes automatically, so this needs no composite index and bills a
     * fraction of a read. It also stays exact if the page cap is ever hit.
     */
    async function renderPayingCard() {
      const usersCol = collection(db, ACCOUNTS);
      try {
        // Both filters are equalities, so a platform-scoped count is served by
        // index merging and still needs no composite index. The denominator has
        // to be re-counted per platform though: userTotalCount spans all of
        // them, and dividing iOS payers by every account would understate iOS.
        const denominator = platformFilter === 'all'
          ? (userTotalCount || userRows.length)
          : (await getCountFromServer(withPlatform(usersCol))).data().count;
        const snap = await getCountFromServer(
          withPlatform(usersCol, where('subscription.isSubscribed', '==', true)),
        );
        if (disposed) return;
        const paying = snap.data().count;
        setText('c-paying', paying);
        setText('c-paying-rate', pct(paying, denominator));
      } catch (e) {
        // Fall back to the loaded rows so the card shows something, but say so
        // rather than presenting a partial tally as if it were the real number.
        console.warn('paying count failed, falling back to loaded rows', e);
        const scoped = userRows.filter(r => matchesPlatform(r.data));
        const paying = scoped.filter(r => isPaying(r.data)).length;
        setText('c-paying', userTruncated ? paying + '+' : paying);
        setText('c-paying-rate', pct(paying, scoped.length));
      }
    }

    /**
     * Per-country breakdown.
     *
     * Accounts come from the users already in memory. Install counts are the
     * one extra query — a count-only aggregation per country, which bills a
     * fraction of a read each and never downloads the documents.
     *
     * `country` is derived from the device locale by the app. Installs and
     * accounts predating that field have none and are pooled into an "Unknown"
     * row rather than being guessed at or hidden, so the totals always
     * reconcile with the Reach cards above.
     */
    async function renderMarkets() {
      const tbody = byId('markets-tbody');
      if (!tbody) return;
      const byCountry = new Map();
      const blank = () => ({ accounts: 0, paying: 0, trial: 0, installs: null, ios: 0, android: 0 });
      // Scoped to the selected platform like every other panel. The ios/android
      // tallies below are the accounts *within* that scope, so under a platform
      // view one side is zero by construction — which is the honest reading, not
      // a bug: it is the same rows the Users table is showing.
      for (const { data } of userRows) {
        if (!matchesPlatform(data)) continue;
        const cc = (typeof data.country === 'string' && data.country.trim()) || '??';
        const t = trialInfo(data);
        const e = byCountry.get(cc) || blank();
        e.accounts++;
        const plat = userPlatform(data);
        if (plat === 'ios') e.ios++;
        else if (plat === 'android') e.android++;
        if (isPaying(data)) e.paying++;
        if (t.state === 'active' || t.state === 'ending') e.trial++;
        byCountry.set(cc, e);
      }

      try {
        const installsCol = collection(db, 'installs');
        const known = [...byCountry.keys()].filter(cc => cc !== '??');
        const counts = await Promise.all(known.map(async cc => {
          try {
            // country + platform are both equalities — index merging, no
            // composite index needed.
            const s = await getCountFromServer(withPlatform(installsCol, where('country', '==', cc)));
            return [cc, s.data().count];
          } catch (e) {
            // One bad country must not blank the whole table.
            console.warn('install count failed for', cc, e);
            return [cc, null];
          }
        }));
        counts.forEach(([cc, n]) => { byCountry.get(cc).installs = n; });

        // Installs that matched no country row — older installs with no country
        // field, plus any market that produced installs but zero accounts.
        // Worth seeing: it is reach with nothing to show for it.
        const total = await getCountFromServer(withPlatform(installsCol));
        const attributed = counts.reduce((s, [, n]) => s + (n || 0), 0);
        const unattributed = Math.max(0, total.data().count - attributed);
        if (unattributed > 0) {
          const e = byCountry.get('??') || blank();
          e.installs = unattributed;
          byCountry.set('??', e);
        }
      } catch (e) {
        console.warn('markets install counts failed', e);
      }
      if (disposed) return;

      const rows = [...byCountry.entries()]
        .sort((a, b) => ((b[1].installs || 0) - (a[1].installs || 0)) || (b[1].accounts - a[1].accounts));

      if (!rows.length) {
        const why = platformFilter === 'all'
          ? 'No accounts yet.'
          : `No ${platformLabel()} accounts yet.`;
        tbody.innerHTML = `<tr><td colspan="8" style="text-align:center;color:#9aa6b0;padding:24px;">${why}</td></tr>`;
        setText('markets-note', '');
        return;
      }

      tbody.innerHTML = rows.map(([cc, e]) => {
        const label = cc === '??'
          ? '<span style="color:#9aa6b0;">Unknown / older builds</span>'
          : `${escapeHtml(countryName(cc))} <span style="color:#9aa6b0;">${escapeHtml(cc)}</span>`;
        return `
          <tr>
            <td>${label}</td>
            <td class="u-count">${e.installs === null ? '—' : e.installs}</td>
            <td class="u-count">${e.accounts}</td>
            <td class="u-count">${e.ios} / ${e.android}</td>
            <td class="u-count">${e.installs ? pct(e.accounts, e.installs) : '—'}</td>
            <td class="u-count">${e.paying}</td>
            <td class="u-total">${pct(e.paying, e.accounts)}</td>
            <td class="u-count">${e.trial}</td>
          </tr>`;
      }).join('');

      // Counted over the scoped rows, not userRows, so the footer agrees with
      // the table above it when a platform is selected.
      const scoped = userRows.filter(r => matchesPlatform(r.data)).length;
      const scope = platformFilter === 'all' ? '' : ` · ${platformLabel()}`;
      setText('markets-note', userTruncated
        ? `${rows.length} market(s)${scope} · ⚠ account columns cover only ${scoped} of ${userTotalCount} accounts`
        : `${rows.length} market(s)${scope} · ${scoped} account(s)`);
    }

    /** ISO code → display name, falling back to the code itself. */
    function countryName(cc) {
      try {
        return new Intl.DisplayNames([navigator.language || 'en'], { type: 'region' }).of(cc) || cc;
      } catch (e) {
        return cc;
      }
    }

    function renderUserRow(uid, u) {
      const totals = u.totals || {};
      const trial = trialInfo(u);
      const lastMs = u.lastActiveAt && u.lastActiveAt.toMillis ? u.lastActiveAt.toMillis() : null;
      const ago = timeAgo(lastMs);
      const freshness = lastMs && (Date.now() - lastMs) < 86400000 ? 'fresh' : 'stale';
      // Accounts are anonymous by default, so most rows fall through to the uid.
      const name = u.name || u.email || uid;
      const plat = userPlatform(u);
      return `
        <tr class="user-row" data-uid="${escapeHtml(uid)}">
          <td class="u-caret">▸</td>
          <td>
            <div class="u-name">${escapeHtml(name)}</div>
            ${u.name && u.email ? `<div class="u-email">${escapeHtml(u.email)}</div>` : ''}
          </td>
          <td><span class="u-plat ${plat}">${plat === 'ios' ? 'iOS' : plat === 'android' ? 'Android' : '—'}</span></td>
          <td class="u-active ${freshness}">${ago ? escapeHtml(ago) : '—'}</td>
          <td class="u-trial ${trial.state}">${escapeHtml(trial.label)}</td>
          <td class="u-count">${num(totals, 'meal')}</td>
          <td class="u-count">${num(totals, 'recipe')}</td>
          <td class="u-count">${num(totals, 'learn')}</td>
          <td class="u-count">${num(totals, 'other')}</td>
          <td class="u-total">${num(totals, 'total')}</td>
        </tr>
        <tr class="user-detail-row" data-detail-uid="${escapeHtml(uid)}" style="display:none;">
          <td colspan="10"></td>
        </tr>`;
    }

    // The rows are written with innerHTML, so they cannot carry React handlers.
    // One delegated listener on the tbody covers every row that will ever be
    // drawn — and, unlike the original's inline onclick, it needs no global.
    const usersTbody = byId('users-tbody');
    const onUsersClick = (ev) => {
      const row = ev.target.closest ? ev.target.closest('tr.user-row') : null;
      if (!row || !usersTbody.contains(row)) return;
      toggleUserPanel(row.dataset.uid);
    };
    if (usersTbody) usersTbody.addEventListener('click', onUsersClick);

    const rowFor = (uid) => root.querySelector(`tr.user-row[data-uid="${CSS.escape(uid)}"]`);
    const detailFor = (uid) => root.querySelector(`tr.user-detail-row[data-detail-uid="${CSS.escape(uid)}"]`);

    function toggleUserPanel(uid) {
      const row = rowFor(uid);
      const detailRow = detailFor(uid);
      if (!row || !detailRow) return;
      const isOpen = detailRow.style.display !== 'none';
      if (isOpen) {
        detailRow.style.display = 'none';
        row.classList.remove('open');
        row.querySelector('.u-caret').textContent = '▸';
        userPanelState[uid] = { ...(userPanelState[uid] || {}), open: false };
        return;
      }
      // No getDoc here on purpose: the panel is built entirely from the
      // daily_activity / activity_log reads below, and the row's own totals
      // came with the users list query. Re-fetching users/{uid} would be a read
      // per expand that nothing consumes.
      openUserPanel(uid);
    }

    async function openUserPanel(uid) {
      const row = rowFor(uid);
      const detailRow = detailFor(uid);
      if (!row || !detailRow) return;
      row.classList.add('open');
      const caret = row.querySelector('.u-caret');
      if (caret) caret.textContent = '▾';
      detailRow.style.display = '';

      const state = userPanelState[uid] || { from: addDays(todayId(), -6), to: todayId() };
      state.open = true;
      userPanelState[uid] = state;

      detailRow.querySelector('td').innerHTML = `
        <div class="user-detail">
          <div class="range-bar" style="margin-bottom:14px;">
            <label>From</label>
            <input type="date" class="u-from" value="${state.from}" />
            <span class="range-sep">→</span>
            <label>To</label>
            <input type="date" class="u-to" value="${state.to}" />
            <button class="preset-btn" data-p="today">Today</button>
            <button class="preset-btn" data-p="7">Last 7 days</button>
            <button class="preset-btn" data-p="30">Last 30 days</button>
          </div>
          <div class="detail-days" data-days-for="${escapeHtml(uid)}"><div class="empty-note" style="flex:1;">Loading…</div></div>
          <div class="section-title" style="margin:14px 2px 8px;">Activity log</div>
          <div class="log-list" data-log-for="${escapeHtml(uid)}"><div class="empty-note">Loading…</div></div>
        </div>`;

      const fromEl = detailRow.querySelector('.u-from');
      const toEl = detailRow.querySelector('.u-to');
      const reload = () => {
        if (!fromEl.value || !toEl.value || fromEl.value > toEl.value) return;
        state.from = fromEl.value; state.to = toEl.value;
        loadUserDetail(uid, state.from, state.to);
      };
      fromEl.addEventListener('change', reload);
      toEl.addEventListener('change', reload);
      detailRow.querySelectorAll('.preset-btn').forEach(b => b.addEventListener('click', () => {
        const p = b.dataset.p, today = todayId();
        state.to = today;
        state.from = p === 'today' ? today : p === '7' ? addDays(today, -6) : addDays(today, -29);
        fromEl.value = state.from; toEl.value = state.to;
        loadUserDetail(uid, state.from, state.to);
      }));
      // The row click that opened this panel bubbles from inside it too, so a
      // click on a date input or a preset would immediately collapse the panel
      // again without this.
      detailRow.addEventListener('click', ev => ev.stopPropagation());

      await loadUserDetail(uid, state.from, state.to);
    }

    async function loadUserDetail(uid, from, to) {
      const daysEl = root.querySelector(`[data-days-for="${CSS.escape(uid)}"]`);
      const logEl = root.querySelector(`[data-log-for="${CSS.escape(uid)}"]`);
      if (!daysEl || !logEl) return;
      try {
        const dayIds = dayRange(from, to, 62);

        const dayDocs = await Promise.all(dayIds.map(async id => {
          const snap = await getDoc(doc(db, ACCOUNTS, uid, 'daily_activity', id));
          return { date: id, ...(snap.exists() ? snap.data() : {}) };
        }));
        if (disposed) return;
        daysEl.innerHTML = dayDocs.map(d => `
          <div class="day-cell">
            <div class="d-date">${d.date.slice(5)}</div>
            <div class="d-total">${num(d, 'total')}</div>
            <div class="d-breakdown">${num(d, 'meal')}m · ${num(d, 'recipe')}r · ${num(d, 'learn')}l · ${num(d, 'other')}o</div>
          </div>`).join('') || '<div class="empty-note" style="flex:1;">No days in range.</div>';

        // `day` is stored as a plain "yyyy-MM-dd" string, so a lexicographic
        // range filter matches the same window as dayIds above.
        //
        // orderBy is on `day` — the same field as the range filter — so this
        // needs no composite index, and the limit is what actually caps the
        // read cost: without it a busy account downloads its entire history for
        // the range just to show the most recent few hundred lines. Ordering
        // within a day is then done client-side on `timestamp`.
        const LOG_LIMIT = 300;
        const logSnap = await getDocs(query(
          collection(db, ACCOUNTS, uid, 'activity_log'),
          where('day', '>=', from),
          where('day', '<=', to),
          orderBy('day', 'desc'),
          limit(LOG_LIMIT),
        ));
        if (disposed) return;
        const entries = logSnap.docs
          .map(d => d.data())
          .sort((a, b) => {
            const am = a.timestamp && a.timestamp.toMillis ? a.timestamp.toMillis() : 0;
            const bm = b.timestamp && b.timestamp.toMillis ? b.timestamp.toMillis() : 0;
            return bm - am;
          });
        const capped = logSnap.size === LOG_LIMIT;

        logEl.innerHTML = entries.length ? (
          (capped ? `<div class="empty-note" style="padding:10px;">Showing the ${LOG_LIMIT} most recent entries — narrow the range to see the rest.</div>` : '') +
          entries.map(e => {
            const when = e.timestamp && e.timestamp.toDate
              ? e.timestamp.toDate().toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })
              : (e.day || '');
            const cat = escapeHtml(e.category || 'other');
            return `
              <div class="log-entry">
                <span class="log-cat ${cat}">${cat}</span>
                <span class="log-when">${escapeHtml(when)}</span>
                <span class="log-desc">${escapeHtml(prettyAction(e.action))}</span>
              </div>`;
          }).join('')
        ) : '<div class="empty-note">No activity logged in this range.</div>';
      } catch (e) {
        console.error('User detail load failed', e);
        daysEl.innerHTML = '<div class="empty-note" style="flex:1;">Could not load — check console.</div>';
        logEl.innerHTML = '';
      }
    }

    // The Refresh button is rendered by React, which cannot see the closures
    // above; this is the handoff.
    const refreshBtn = byId('an-refresh');
    const loadAll = () => { loadOverview(); loadUsers(); };
    if (refreshBtn) refreshBtn.addEventListener('click', loadAll);

    applyPreset('today');   // also triggers the first loadOverview()
    loadUsers();

    return () => {
      // Every in-flight query resolves into DOM this component no longer owns,
      // so the guard matters more than the listener teardown does.
      disposed = true;
      if (usersTbody) usersTbody.removeEventListener('click', onUsersClick);
      if (refreshBtn) refreshBtn.removeEventListener('click', loadAll);
    };
  }, [loggedIn]);

  // Same session guard the rest of the admin section uses (see nav.js).
  if (!loggedIn) {
    return <Navigate to='/admin/login' replace />;
  }

  return (
    <div className='an-root' ref={rootRef}>
      <div className='an-topbar'>
        <h2>Analytics</h2>
        <div className='an-spacer' />
        <span id='an-last-updated' />
        <button className='an-refresh-btn' id='an-refresh' type='button'>Refresh</button>
      </div>

      <div className='an-main'>
        {/* Governs EVERY number below: the Reach cards, the activity counters,
            the funnel, the chart, Markets and the Users table. It sits above the
            first section title rather than inside one so it does not read as a
            filter belonging to a single card group. */}
        <div className='range-bar' id='platform-bar'>
          <label>Platform</label>
          <button className='preset-btn plat-btn active' data-plat='all' type='button'>All</button>
          <button className='preset-btn plat-btn' data-plat='ios' type='button'>iOS</button>
          <button className='preset-btn plat-btn' data-plat='android' type='button'>Android</button>
          <div className='range-spacer' />
          <span className='range-label' id='platform-note' />
        </div>

        <div className='section-title'>Reach</div>
        <div className='an-cards'>
          <div className='an-card an-accent'>
            <div className='label'>Total installs</div>
            <div className='value' id='c-total-installs'>–</div>
            <div className='sub'>all time</div>
          </div>
          <div className='an-card'>
            <div className='label'>New installs today</div>
            <div className='value' id='c-new-installs'>–</div>
            <div className='sub'>since midnight UTC</div>
          </div>
          <div className='an-card'>
            <div className='label'>Active users today</div>
            <div className='value' id='c-active-users'>–</div>
            <div className='sub'>opened the app today</div>
          </div>
          <div className='an-card'>
            <div className='label'>Paying customers</div>
            <div className='value' id='c-paying'>–</div>
            <div className='sub'><span id='c-paying-rate'>–</span> of accounts</div>
          </div>
        </div>

        <div className='section-title'>Activity</div>

        <div className='range-bar'>
          <label>From</label>
          <input type='date' id='range-from' />
          <span className='range-sep'>→</span>
          <label>To</label>
          <input type='date' id='range-to' />
          <button className='preset-btn' data-preset='today' type='button'>Today</button>
          <button className='preset-btn' data-preset='7' type='button'>Last 7 days</button>
          <button className='preset-btn' data-preset='30' type='button'>Last 30 days</button>
          <button className='preset-btn' data-preset='month' type='button'>This month</button>
          <div className='range-spacer' />
          <span className='range-label' id='range-label' />
        </div>

        <div className='an-cards'>
          <div className='an-card'>
            <div className='label'>Meals logged</div>
            <div className='value' id='c-meals-range'>–</div>
            <div className='sub'><span id='c-meals-total'>–</span> all-time</div>
          </div>
          <div className='an-card'>
            <div className='label'>Food scans</div>
            <div className='value' id='c-scans-range'>–</div>
            <div className='sub'><span id='c-scans-total'>–</span> all-time</div>
          </div>
          <div className='an-card'>
            <div className='label'>Recipe views</div>
            <div className='value' id='c-recipes-range'>–</div>
            <div className='sub'><span id='c-recipe-likes'>–</span> likes in range</div>
          </div>
          <div className='an-card'>
            <div className='label'>Meal plans edited</div>
            <div className='value' id='c-plans-range'>–</div>
            <div className='sub'><span id='c-plans-total'>–</span> all-time</div>
          </div>
          <div className='an-card'>
            <div className='label'>Quizzes taken</div>
            <div className='value' id='c-quizzes-range'>–</div>
            <div className='sub'><span id='c-learn-range'>–</span> content views</div>
          </div>
          <div className='an-card'>
            <div className='label'>Product views</div>
            <div className='value' id='c-products-range'>–</div>
            <div className='sub'><span id='c-products-total'>–</span> all-time</div>
          </div>
          <div className='an-card'>
            <div className='label'>Sign-ins</div>
            <div className='value' id='c-logins-range'>–</div>
            <div className='sub'><span id='c-logins-total'>–</span> all-time</div>
          </div>
        </div>

        <div className='chart-card'>
          <h3>Daily breakdown</h3>
          <div className='legend'>
            <b className='ml'>■</b> Meals logged &nbsp;·&nbsp;
            <b className='sc'>■</b> Recipe views &nbsp;·&nbsp;
            <b className='rc'>■</b> Content views
          </div>
          <div className='chart-wrap'><svg id='trend-chart' height='200' /></div>
        </div>

        <p style={{ fontSize: 12, color: '#9aa6b0', marginTop: 8 }}>
          Install &amp; active-user counts come from the app&apos;s own anonymous usage pings.
          Official store install totals live in the Play Console / App Store Connect.
        </p>

        {/* Funnel + markets. Both are driven by the date range above. */}
        <div className='section-title' style={{ marginTop: 30 }}>Funnel</div>
        <div className='an-cards' id='funnel-cards'>
          <div className='an-card'>
            <div className='label'>Installs</div>
            <div className='value' id='f-installs'>–</div>
            <div className='sub'>in range</div>
          </div>
          <div className='an-card'>
            <div className='label'>Signups</div>
            <div className='value' id='f-signups'>–</div>
            <div className='sub'><span id='f-signup-rate'>–</span> of installs · anonymous</div>
          </div>
          <div className='an-card'>
            <div className='label'>Accounts saved</div>
            <div className='value' id='f-accounts'>–</div>
            <div className='sub'><span id='f-account-rate'>–</span> of signups · can restore</div>
          </div>
          <div className='an-card'>
            <div className='label'>Activated</div>
            <div className='value' id='f-activations'>–</div>
            <div className='sub'><span id='f-activation-rate'>–</span> of signups · logged a meal</div>
          </div>
          <div className='an-card'>
            <div className='label'>Paywall views</div>
            <div className='value' id='f-paywall'>–</div>
            <div className='sub'><span id='f-tap-rate'>–</span> tapped subscribe</div>
          </div>
          <div className='an-card'>
            <div className='label'>Hard-wall hits</div>
            <div className='value' id='f-gated'>–</div>
            <div className='sub'><span id='f-gated-share'>–</span> of paywall views · trial expired</div>
          </div>
          <div className='an-card'>
            <div className='label'>Purchases</div>
            <div className='value' id='f-purchases'>–</div>
            <div className='sub'><span id='f-purchase-rate'>–</span> of subscribe taps</div>
          </div>
          <div className='an-card'>
            <div className='label'>Failed purchases</div>
            <div className='value' id='f-failures'>–</div>
            <div className='sub'>excludes user cancels</div>
          </div>
        </div>

        <div className='section-row'>
          <div className='section-title'>Markets</div>
          <span className='range-label' id='markets-note' />
        </div>
        <div className='users-card'>
          <table className='users-table'>
            <thead>
              <tr>
                <th>Country</th>
                <th>Installs</th>
                <th>Accounts</th>
                <th>iOS / Android</th>
                <th>Signup rate</th>
                <th>Paying</th>
                <th>Paid rate</th>
                <th>On trial</th>
              </tr>
            </thead>
            <tbody id='markets-tbody'>
              <tr><td colSpan='8' style={{ textAlign: 'center', color: '#9aa6b0', padding: 24 }}>Loading…</td></tr>
            </tbody>
          </table>
        </div>

        <div className='section-row'>
          <div className='section-title'>Users</div>
          <span className='range-label' id='users-summary' />
        </div>
        {/* Filters run over the already-fetched rows — no extra Firestore reads. */}
        <div className='range-bar' id='users-filter'>
          <button className='preset-btn uf-btn active' data-uf='all' type='button'>All</button>
          <button className='preset-btn uf-btn' data-uf='trial' type='button'>On trial</button>
          <button className='preset-btn uf-btn' data-uf='premium' type='button'>Paying</button>
          <button className='preset-btn uf-btn' data-uf='expired' type='button'>Trial expired</button>
        </div>
        <div className='users-card'>
          <table className='users-table'>
            <thead>
              <tr>
                <th />
                <th>Account</th>
                <th>Platform</th>
                <th>Last used app</th>
                <th>Trial</th>
                <th>Meals</th>
                <th>Recipes</th>
                <th>Learn</th>
                <th>Other</th>
                <th>Total activity</th>
              </tr>
            </thead>
            <tbody id='users-tbody'>
              <tr><td colSpan='10' style={{ textAlign: 'center', color: '#9aa6b0', padding: 24 }}>Loading…</td></tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
